import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Save, Printer, FileText, User, Calendar, Edit2, FileDown, X, Share2, Trash2, RotateCcw, Wallet, Receipt, TrendingUp } from "lucide-react";
import { generatePDFFromHTML, shareWhatsApp } from "../../utils/pdfShare";
import { printHTML } from "../../utils/printHTML";
import { useDrafts } from "../../contexts/DraftsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const todayISO = () => new Date().toISOString().slice(0, 10);
const todayStr = () => new Date().toLocaleDateString("en-GB");

// ===== Arabic words for amounts =====
const ones = ["", "واحد", "اثنان", "ثلاثة", "أربعة", "خمسة", "ستة", "سبعة", "ثمانية", "تسعة", "عشرة",
  "أحد عشر", "اثنا عشر", "ثلاثة عشر", "أربعة عشر", "خمسة عشر", "ستة عشر", "سبعة عشر", "ثمانية عشر", "تسعة عشر"];
const tens = ["", "عشرة", "عشرون", "ثلاثون", "أربعون", "خمسون", "ستون", "سبعون", "ثمانون", "تسعون"];
const hundreds = ["", "مئة", "مئتان", "ثلاثمئة", "أربعمئة", "خمسمئة", "ستمئة", "سبعمئة", "ثمانمئة", "تسعمئة"];
const sub999 = (n) => {
  if (n === 0) return "";
  const out = [];
  const h = Math.floor(n / 100); const r = n % 100;
  if (h) out.push(hundreds[h]);
  if (r < 20) { if (r) out.push(ones[r]); }
  else { const t = Math.floor(r / 10); const o = r % 10;
    out.push(o ? `${ones[o]} و${tens[t]}` : tens[t]); }
  return out.join(" و");
};
const toArabicWords = (num) => {
  num = Math.floor(Math.abs(Number(num) || 0));
  if (num === 0) return "صفر";
  const parts = [];
  const billions = Math.floor(num / 1_000_000_000);
  const millions = Math.floor((num % 1_000_000_000) / 1_000_000);
  const thousands = Math.floor((num % 1_000_000) / 1_000);
  const rest = num % 1_000;
  if (billions) parts.push(`${sub999(billions)} مليار`);
  if (millions) {
    if (millions === 1) parts.push("مليون");
    else if (millions === 2) parts.push("مليونان");
    else if (millions <= 10) parts.push(`${sub999(millions)} ملايين`);
    else parts.push(`${sub999(millions)} مليون`);
  }
  if (thousands) {
    if (thousands === 1) parts.push("ألف");
    else if (thousands === 2) parts.push("ألفان");
    else if (thousands <= 10) parts.push(`${sub999(thousands)} آلاف`);
    else parts.push(`${sub999(thousands)} ألف`);
  }
  if (rest) parts.push(sub999(rest));
  return `${parts.join(" و")} دينار عراقي فقط لا غير`;
};

// ===== Balance Badge (handles negative = customer has credit) =====
const BalanceBadge = ({ value, label = "الرصيد" }) => {
  const v = Number(value || 0);
  let bg, prefix, suffix;
  if (v > 0)      { bg = "bg-[#E85D45]"; prefix = "";  suffix = "د.ع (دين)"; }
  else if (v < 0) { bg = "bg-[#1E3A5F]"; prefix = "-"; suffix = "د.ع (له رصيد)"; }
  else            { bg = "bg-[#7ED321]"; prefix = "";  suffix = "د.ع (مسدد)"; }
  return (
    <div className={`${bg} text-white rounded-xl p-2.5`}>
      <p className="text-[10px] opacity-90">{label}</p>
      <p className="font-black text-lg" dir="ltr">{prefix}{fmt(Math.abs(v))} {suffix}</p>
    </div>
  );
};

// ===== Print Receipt helper (uses hidden-iframe helper to bypass popup blockers) =====
const printReceipt = (r) => {
  const styleLinks = Array.from(document.querySelectorAll('link[rel="stylesheet"], style')).map((el) => el.outerHTML).join("\n");
  const nbColor = Number(r.new_balance) > 0 ? "#E85D45" : Number(r.new_balance) < 0 ? "#1E3A5F" : "#7ED321";
  const nbLabel = Number(r.new_balance) > 0 ? "دين" : Number(r.new_balance) < 0 ? "له رصيد" : "مسدد";
  const nbValue = `${Number(r.new_balance) < 0 ? "-" : ""}${fmt(Math.abs(r.new_balance))} د.ع`;
  const html = `<!DOCTYPE html><html dir="rtl" lang="ar"><head><meta charset="UTF-8"><title>سند قبض #${r.receipt_no}</title>${styleLinks}<style>
    html,body{margin:0;padding:0;background:#fff;color:#000;font-family:'Tajawal',sans-serif}
    body{padding:10mm 8mm}@page{size:A5;margin:5mm}
  </style></head><body>
    <div style="border:3px solid #1E3A5F;border-radius:10px;padding:15px;max-width:520px;margin:0 auto;">
      <div style="text-align:center;border-bottom:2px solid #1E3A5F;padding-bottom:8px;margin-bottom:12px">
        <h1 style="margin:0;color:#1E3A5F;font-size:22pt">جُملَتي</h1>
        <p style="margin:2px 0;font-size:11pt;font-weight:bold">إدارة مكتب الأمين</p>
        <p style="margin:2px 0;font-size:9pt">العراق — كربلاء المقدسة — 07717637525 — 07749330887</p>
      </div>
      <div style="text-align:center;background:#1E3A5F;color:#fff;padding:6px;border-radius:8px;margin-bottom:10px;font-size:14pt;font-weight:bold">
        سند قبض${r.edited_at ? ' (معدّل)' : ''}
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:11pt">
        <tr><td style="border:1px solid #000;padding:5px;background:#f0f0f0;font-weight:bold;width:30%">رقم السند</td>
            <td style="border:1px solid #000;padding:5px;font-weight:bold">${r.receipt_no}</td>
            <td style="border:1px solid #000;padding:5px;background:#f0f0f0;font-weight:bold;width:25%">التاريخ</td>
            <td style="border:1px solid #000;padding:5px">${new Date(r.created_at).toLocaleDateString("en-GB")}</td></tr>
        <tr><td style="border:1px solid #000;padding:5px;background:#f0f0f0;font-weight:bold">اسم الزبون</td>
            <td colspan="3" style="border:1px solid #000;padding:5px">${r.user_name}${r.market_name ? ' — ' + r.market_name : ''}</td></tr>
        <tr><td style="border:1px solid #000;padding:5px;background:#f0f0f0;font-weight:bold">رقم الهاتف</td>
            <td colspan="3" style="border:1px solid #000;padding:5px;font-family:monospace" dir="ltr">${r.user_phone || '—'}</td></tr>
        <tr><td style="border:1px solid #000;padding:5px;background:#f0f0f0;font-weight:bold">المبلغ المُسلَّم</td>
            <td style="border:1px solid #000;padding:5px;font-weight:bold;font-size:14pt;color:#7ED321" dir="ltr">${fmt(r.amount)} د.ع</td>
            <td style="border:1px solid #000;padding:5px;background:#f0f0f0;font-weight:bold">الخصم</td>
            <td style="border:1px solid #000;padding:5px" dir="ltr">${fmt(r.discount || 0)} د.ع</td></tr>
        <tr><td style="border:1px solid #000;padding:5px;background:#f0f0f0;font-weight:bold">المبلغ كتابةً</td>
            <td colspan="3" style="border:1px solid #000;padding:5px;font-weight:bold">${toArabicWords(r.amount)}</td></tr>
        <tr><td style="border:1px solid #000;padding:5px;background:#f0f0f0;font-weight:bold">الرصيد السابق</td>
            <td style="border:1px solid #000;padding:5px;font-weight:bold;color:#E85D45" dir="ltr">${fmt(r.previous_balance)} د.ع</td>
            <td style="border:1px solid #000;padding:5px;background:#f0f0f0;font-weight:bold">الرصيد الحالي</td>
            <td style="border:1px solid #000;padding:5px;font-weight:bold;color:${nbColor}" dir="ltr">${nbValue} (${nbLabel})</td></tr>
        ${r.notes ? `<tr><td style="border:1px solid #000;padding:5px;background:#f0f0f0;font-weight:bold">ملاحظات</td><td colspan="3" style="border:1px solid #000;padding:5px">${r.notes}</td></tr>` : ''}
      </table>
      <div style="margin-top:30px;display:flex;justify-content:space-between;font-size:10pt">
        <div style="text-align:center;width:45%"><div style="border-top:1px solid #000;padding-top:5px">توقيع المُسلِّم</div></div>
        <div style="text-align:center;width:45%"><div style="border-top:1px solid #000;padding-top:5px">توقيع المُستلِم</div></div>
      </div>
    </div>
  </body></html>`;
  printHTML(html, { title: `سند قبض #${r.receipt_no}` });
};

// ===== Generate HTML of a single receipt (for PDF & sharing) =====
const receiptHTML = (r) => {
  const nbColor = Number(r.new_balance) > 0 ? "#E85D45" : Number(r.new_balance) < 0 ? "#1E3A5F" : "#7ED321";
  const nbLabel = Number(r.new_balance) > 0 ? "دين" : Number(r.new_balance) < 0 ? "له رصيد" : "مسدد";
  const nbValue = `${Number(r.new_balance) < 0 ? "-" : ""}${fmt(Math.abs(r.new_balance))} د.ع`;
  return `<div dir="rtl" style="font-family:Tajawal,sans-serif;border:3px solid #1E3A5F;border-radius:10px;padding:15px;max-width:520px;margin:0 auto;background:#fff;color:#000;">
    <div style="text-align:center;border-bottom:2px solid #1E3A5F;padding-bottom:8px;margin-bottom:12px">
      <h1 style="margin:0;color:#1E3A5F;font-size:22pt">جُملَتي</h1>
      <p style="margin:2px 0;font-size:11pt;font-weight:bold">إدارة مكتب الأمين</p>
      <p style="margin:2px 0;font-size:9pt">العراق — كربلاء المقدسة — 07717637525 — 07749330887</p>
    </div>
    <div style="text-align:center;background:#1E3A5F;color:#fff;padding:6px;border-radius:8px;margin-bottom:10px;font-size:14pt;font-weight:bold">
      سند قبض${r.edited_at ? ' (معدّل)' : ''}
    </div>
    <table style="width:100%;border-collapse:collapse;font-size:11pt">
      <tr><td style="border:1px solid #000;padding:5px;background:#f0f0f0;font-weight:bold;width:30%">رقم السند</td>
          <td style="border:1px solid #000;padding:5px;font-weight:bold">${r.receipt_no}</td>
          <td style="border:1px solid #000;padding:5px;background:#f0f0f0;font-weight:bold;width:25%">التاريخ</td>
          <td style="border:1px solid #000;padding:5px">${new Date(r.created_at).toLocaleDateString("en-GB")}</td></tr>
      <tr><td style="border:1px solid #000;padding:5px;background:#f0f0f0;font-weight:bold">اسم الزبون</td>
          <td colspan="3" style="border:1px solid #000;padding:5px">${r.user_name}${r.market_name ? ' — ' + r.market_name : ''}</td></tr>
      <tr><td style="border:1px solid #000;padding:5px;background:#f0f0f0;font-weight:bold">رقم الهاتف</td>
          <td colspan="3" style="border:1px solid #000;padding:5px;font-family:monospace" dir="ltr">${r.user_phone || '—'}</td></tr>
      <tr><td style="border:1px solid #000;padding:5px;background:#f0f0f0;font-weight:bold">المبلغ المُسلَّم</td>
          <td style="border:1px solid #000;padding:5px;font-weight:bold;font-size:14pt;color:#7ED321" dir="ltr">${fmt(r.amount)} د.ع</td>
          <td style="border:1px solid #000;padding:5px;background:#f0f0f0;font-weight:bold">الخصم</td>
          <td style="border:1px solid #000;padding:5px" dir="ltr">${fmt(r.discount || 0)} د.ع</td></tr>
      <tr><td style="border:1px solid #000;padding:5px;background:#f0f0f0;font-weight:bold">المبلغ كتابةً</td>
          <td colspan="3" style="border:1px solid #000;padding:5px;font-weight:bold">${toArabicWords(r.amount)}</td></tr>
      <tr><td style="border:1px solid #000;padding:5px;background:#f0f0f0;font-weight:bold">الرصيد السابق</td>
          <td style="border:1px solid #000;padding:5px;font-weight:bold;color:#E85D45" dir="ltr">${fmt(r.previous_balance)} د.ع</td>
          <td style="border:1px solid #000;padding:5px;background:#f0f0f0;font-weight:bold">الرصيد الحالي</td>
          <td style="border:1px solid #000;padding:5px;font-weight:bold;color:${nbColor}" dir="ltr">${nbValue} (${nbLabel})</td></tr>
      ${r.notes ? `<tr><td style="border:1px solid #000;padding:5px;background:#f0f0f0;font-weight:bold">ملاحظات</td><td colspan="3" style="border:1px solid #000;padding:5px">${r.notes}</td></tr>` : ''}
    </table>
  </div>`;
};

// ===== Download a single receipt as PDF =====
const downloadReceiptPDF = async (r) => {
  try {
    await generatePDFFromHTML(receiptHTML(r), `سند_قبض_${r.receipt_no}.pdf`, { format: "a5" });
    toast.success("تم تنزيل PDF");
  } catch (e) {
    toast.error("فشل إنشاء PDF");
  }
};

// ===== Share receipt via WhatsApp =====
const shareReceiptWA = (r) => {
  const nbLabel = Number(r.new_balance) > 0 ? "دين متبقي" : Number(r.new_balance) < 0 ? "له رصيد" : "مسدد";
  const nbValue = fmt(Math.abs(Number(r.new_balance) || 0));
  const text =
    `*سند قبض — جُملَتي*\n` +
    `رقم السند: ${r.receipt_no}\n` +
    `التاريخ: ${new Date(r.created_at).toLocaleDateString("en-GB")}\n` +
    `الزبون: ${r.user_name}${r.market_name ? " — " + r.market_name : ""}\n` +
    `المبلغ المُسلَّم: ${fmt(r.amount)} د.ع\n` +
    (Number(r.discount) > 0 ? `الخصم: ${fmt(r.discount)} د.ع\n` : "") +
    `الرصيد السابق: ${fmt(r.previous_balance)} د.ع\n` +
    `الرصيد الحالي: ${nbValue} د.ع (${nbLabel})\n` +
    (r.notes ? `ملاحظات: ${r.notes}\n` : "") +
    `\n— إدارة مكتب الأمين —`;
  shareWhatsApp(text, r.user_phone);
};

// ===== Sub-tab: Create new receipt =====
const CreateReceipt = ({ onSaved }) => {
  const { clearDirty, saveDraft, loadDraft } = useDrafts();
  const stash = useRef(loadDraft("receipt-voucher") || null).current;
  const [searchQ, setSearchQ] = useState(stash?.searchQ || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [selected, setSelected] = useState(stash?.selected || null);
  const [amount, setAmount] = useState(stash?.amount || "");
  const [discount, setDiscount] = useState(stash?.discount || "");
  const [notes, setNotes] = useState(stash?.notes || "");
  const [busy, setBusy] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const hasContent = !!(selected || amount.trim() || discount.trim() || notes.trim());
    if (hasContent) {
      saveDraft("receipt-voucher", "سند قبض قيد الإدخال", { searchQ, selected, amount, discount, notes });
    }
  }, [searchQ, selected, amount, discount, notes, saveDraft]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!searchQ.trim()) { setSuggestions([]); return; }
      try {
        const { data } = await axios.get(`${API}/admin/customer-lookup`, { params: { q: searchQ } });
        setSuggestions(data || []);
        setShowSuggest(true);
      } catch {/**/}
    }, 300);
  }, [searchQ]);

  const pickCustomer = (c) => {
    setSelected(c);
    setSearchQ(`${c.full_name}${c.market_name ? " — " + c.market_name : ""}`);
    setShowSuggest(false);
  };

  const reset = () => {
    setSelected(null); setSearchQ(""); setAmount(""); setDiscount(""); setNotes(""); setSuggestions([]); setLastReceipt(null);
    clearDirty("receipt-voucher");
  };

  const totalPayment = Number(amount || 0) + Number(discount || 0);
  const prevBal = Number(selected?.customer_debt || 0);
  const newBal = prevBal - totalPayment; // السماح بالسالب

  const handleSave = async () => {
    if (!selected) { toast.error("اختر زبون أولاً"); return; }
    if (Number(amount || 0) <= 0) { toast.error("أدخل المبلغ"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/receipts`, {
        customer_id: selected.id,
        amount: Number(amount || 0),
        discount: Number(discount || 0),
        notes,
      });
      const sign = data.new_balance < 0 ? "له رصيد " : data.new_balance > 0 ? "دين " : "مسدد ";
      toast.success(`تم حفظ السند #${data.receipt_no}. ${sign}${fmt(Math.abs(data.new_balance))} د.ع`);
      setLastReceipt(data);
      setSelected({ ...selected, customer_debt: data.new_balance });
      clearDirty("receipt-voucher");
      if (onSaved) onSaved(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "خطأ");
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="relative mb-4">
        <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5C6E64]" />
        <input type="text" value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
          placeholder="ابحث باسم الزبون / الماركت / الهاتف / رقم الايباد..."
          data-testid="receipt-search"
          className="w-full pr-12 pl-4 py-3 rounded-2xl border-2 border-[#1E3A5F] bg-white text-lg" />
        {showSuggest && suggestions.length > 0 && (
          <div className="absolute top-full mt-1 right-0 left-0 z-10 bg-white border-2 border-[#3B82F6] rounded-2xl shadow-2xl max-h-80 overflow-auto">
            {suggestions.map((c) => (
              <button key={c.id} onClick={() => pickCustomer(c)}
                data-testid={`pick-customer-${c.id}`}
                className="w-full text-right p-3 hover:bg-[#F0F9FF] border-b last:border-b-0 flex justify-between items-center">
                <div>
                  <p className="font-bold text-[#111814]">{c.full_name}{c.market_name && ` — ${c.market_name}`}</p>
                  <p className="text-xs text-[#5C6E64]" dir="ltr">{c.phone}</p>
                </div>
                <span className={`font-bold text-sm ${c.customer_debt > 0 ? "text-[#E85D45]" : c.customer_debt < 0 ? "text-[#1E3A5F]" : "text-[#7ED321]"}`} dir="ltr">
                  {c.customer_debt < 0 ? "-" : ""}{fmt(Math.abs(c.customer_debt))} د.ع
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <>
          {/* Credit-due banner — shown only when the customer is OWED money
              by us (negative customer_debt). Mirrors the user's words:
              "السوق فلان يطلبكم X". */}
          {Number(selected.customer_debt) < 0 && (
            <div
              className="mb-3 rounded-2xl px-4 py-3 flex items-center justify-between gap-3 shadow-sm"
              style={{ background: "linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)", border: "2px solid #1D4ED8" }}
              data-testid="customer-credit-banner"
            >
              <div className="text-right">
                <p className="text-[11px] font-bold text-[#1E3A5F] opacity-80">رصيد دائن للزبون — يُخصم تلقائياً من الفاتورة القادمة</p>
                <p className="font-black text-lg text-[#1E3A5F] leading-tight">
                  {selected.market_name ? `سوق "${selected.market_name}"` : selected.full_name} يطلبكم
                </p>
              </div>
              <p className="font-black text-2xl text-[#1D4ED8]" dir="ltr">
                {fmt(Math.abs(Number(selected.customer_debt)))} د.ع
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
            <div className="bg-[#1E3A5F] text-white rounded-xl p-2.5">
              <p className="text-[10px] opacity-80 flex items-center gap-1"><User className="w-3 h-3" />الزبون</p>
              <p className="font-bold text-sm">{selected.full_name}</p>
            </div>
            <BalanceBadge value={prevBal} label="الرصيد السابق" />
            <div data-testid="new-balance-display">
              <BalanceBadge value={newBal} label="الرصيد بعد السداد" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div>
              <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">المبلغ (دينار) <span className="text-[#E85D45]">*</span></label>
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                data-testid="receipt-amount" placeholder="0"
                className="w-full px-4 py-3 rounded-xl border-2 border-[#3B82F6] bg-white text-xl font-bold text-left" dir="ltr" />
            </div>
            <div>
              <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">الخصم (اختياري)</label>
              <input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)}
                data-testid="receipt-discount" placeholder="0"
                className="w-full px-4 py-3 rounded-xl border-2 border-[#E4EBE6] bg-white text-lg text-left" dir="ltr" />
            </div>
          </div>

          <div className="bg-[#F0F9FF] border border-[#3B82F6]/30 rounded-xl p-3 mb-3 text-sm">
            <p className="text-[#1E3A5F]"><b>المبلغ كتابةً:</b> {toArabicWords(amount)}</p>
          </div>

          <div>
            <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">الملاحظات</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              data-testid="receipt-notes"
              className="w-full px-4 py-2 rounded-xl border border-[#E4EBE6] bg-white text-sm resize-none" />
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <button onClick={handleSave} disabled={busy} data-testid="receipt-save"
              className="flex-1 bg-[#1E3A5F] hover:bg-[#152a45] text-white font-bold py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-50">
              <Save className="w-4 h-4" />{busy ? "جاري الحفظ..." : "حفظ السند"}
            </button>
            {lastReceipt && (
              <button onClick={() => printReceipt(lastReceipt)} data-testid="receipt-print"
                className="bg-[#7ED321] hover:bg-[#6cb91d] text-white font-bold py-3 px-5 rounded-full flex items-center gap-2">
                <Printer className="w-4 h-4" />طباعة
              </button>
            )}
            {lastReceipt && (
              <button onClick={() => downloadReceiptPDF(lastReceipt)} data-testid="receipt-pdf"
                className="bg-[#E85D45] hover:bg-[#c14a36] text-white font-bold py-3 px-5 rounded-full flex items-center gap-2">
                <FileDown className="w-4 h-4" />PDF
              </button>
            )}
            {lastReceipt && (
              <button onClick={() => shareReceiptWA(lastReceipt)} data-testid="receipt-whatsapp"
                className="bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold py-3 px-5 rounded-full flex items-center gap-2">
                <Share2 className="w-4 h-4" />واتساب
              </button>
            )}
            <button onClick={reset} data-testid="receipt-new"
              className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-3 px-5 rounded-full">جديد</button>
          </div>
        </>
      )}

      {lastReceipt && (
        <div className="bg-gradient-to-l from-[#7ED321] to-[#5fa617] text-white rounded-2xl p-4 text-center mt-4" data-testid="last-receipt-summary">
          <p className="text-sm opacity-90">✅ آخر سند محفوظ</p>
          <p className="font-bold text-2xl mt-1">#{lastReceipt.receipt_no} — {lastReceipt.user_name}</p>
          <p className="text-sm mt-1" dir="ltr">{fmt(lastReceipt.amount)} د.ع</p>
        </div>
      )}
    </>
  );
};

// ===== Sub-tab: Edit Modal =====
const EditReceiptModal = ({ receipt, onClose, onUpdated }) => {
  const [amount, setAmount] = useState(String(receipt.amount || ""));
  const [discount, setDiscount] = useState(String(receipt.discount || ""));
  const [notes, setNotes] = useState(receipt.notes || "");
  // Seed the date picker with the current receipt's Baghdad date so the
  // admin can shift it backward/forward. Falls back to today if the
  // receipt lacks a timestamp for some reason.
  const initDate = (() => {
    try {
      if (!receipt.created_at) return new Date().toISOString().slice(0, 10);
      const d = new Date(receipt.created_at);
      // Add 3h to get Baghdad wall-clock date
      const bd = new Date(d.getTime() + 3 * 3600 * 1000);
      return bd.toISOString().slice(0, 10);
    } catch { return new Date().toISOString().slice(0, 10); }
  })();
  const [createdDate, setCreatedDate] = useState(initDate);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (Number(amount) <= 0) { toast.error("المبلغ يجب أن يكون أكبر من صفر"); return; }
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/admin/receipts/${receipt.id}`, {
        amount: Number(amount),
        discount: Number(discount || 0),
        notes,
        // Only send if the admin actually changed it — keeps the payload
        // idempotent for pure-amount edits.
        ...(createdDate !== initDate ? { created_date: createdDate } : {}),
      });
      toast.success(`تم تعديل السند #${data.receipt_no}`);
      onUpdated && onUpdated(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "خطأ");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="edit-receipt-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-5" dir="rtl">
        <div className="flex justify-between items-center mb-4 pb-3 border-b-2 border-[#1E3A5F]/20">
          <h3 className="font-bold text-xl text-[#1E3A5F] flex items-center gap-2">
            <Edit2 className="w-5 h-5" />تعديل سند #{receipt.receipt_no}
          </h3>
          <button onClick={onClose} className="text-[#5C6E64] hover:text-[#E85D45]" data-testid="edit-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-[#5C6E64] mb-3">
          <b>{receipt.user_name}</b>{receipt.market_name && ` — ${receipt.market_name}`}
        </p>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">المبلغ <span className="text-[#E85D45]">*</span></label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
              data-testid="edit-amount" dir="ltr"
              className="w-full px-3 py-2 rounded-xl border-2 border-[#3B82F6] bg-white text-lg font-bold" />
          </div>
          <div>
            <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">الخصم</label>
            <input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)}
              data-testid="edit-discount" dir="ltr"
              className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] bg-white text-lg" />
          </div>
        </div>
        <div className="mb-3">
          <label className="text-xs font-bold text-[#1E3A5F] mb-1 block flex items-center gap-1">
            📅 تاريخ السند
            {createdDate !== initDate && (
              <span className="text-[10px] text-orange-600 font-black bg-orange-50 px-1.5 py-0.5 rounded">
                تم تغييره
              </span>
            )}
          </label>
          <input
            type="date"
            value={createdDate}
            onChange={(e) => setCreatedDate(e.target.value)}
            data-testid="edit-created-date"
            className="w-full px-3 py-2 rounded-xl border-2 border-[#3B82F6] bg-white text-sm font-bold focus:outline-none focus:border-[#1E3A5F]"
            dir="ltr"
          />
          <p className="text-[10px] text-[#5C6E64] mt-1">
            بتوقيت بغداد — تقدر تخليه اليوم، امس، أو أي تاريخ ثاني
          </p>
        </div>
        <div className="mb-3">
          <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">الملاحظات</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
            data-testid="edit-notes"
            className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] bg-white text-sm resize-none" />
        </div>
        <div className="flex gap-2">
          <button onClick={save} disabled={busy} data-testid="edit-save"
            className="flex-1 bg-[#1E3A5F] hover:bg-[#152a45] text-white font-bold py-2 rounded-full disabled:opacity-50">
            {busy ? "جاري الحفظ..." : "حفظ التعديل"}
          </button>
          <button onClick={onClose} className="bg-[#F3F6F4] text-[#5C6E64] font-bold py-2 px-4 rounded-full">إلغاء</button>
        </div>
      </div>
    </div>
  );
};

// ===== Sub-tab: Daily Report =====
const DailyReport = () => {
  const [date, setDate] = useState(todayISO());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/receipts/daily-report`, { params: { date: d } });
      setReport(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "خطأ");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(date); }, [date, load]);

  const reportHTML = () => {
    if (!report) return "";
    const rows = report.receipts.map((r) => {
      const iraqTime = r.created_at ? (() => {
        try {
          return new Date(r.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Baghdad" });
        } catch { return "—"; }
      })() : "—";
      return `
      <tr>
        <td style="border:1px solid #000;padding:4px;text-align:center">${r.receipt_no}</td>
        <td style="border:1px solid #000;padding:4px;text-align:center;font-family:monospace;font-weight:bold;color:#1E3A5F" dir="ltr">${iraqTime}</td>
        <td style="border:1px solid #000;padding:4px">${r.user_name}${r.market_name ? ' — ' + r.market_name : ''}</td>
        <td style="border:1px solid #000;padding:4px;font-family:monospace" dir="ltr">${r.user_phone || '—'}</td>
        <td style="border:1px solid #000;padding:4px;text-align:left;color:#7ED321;font-weight:bold" dir="ltr">${fmt(r.amount)}</td>
        <td style="border:1px solid #000;padding:4px;text-align:left" dir="ltr">${fmt(r.discount || 0)}</td>
        <td style="border:1px solid #000;padding:4px;text-align:left;font-weight:bold;color:${Number(r.new_balance)>0?'#E85D45':Number(r.new_balance)<0?'#1E3A5F':'#7ED321'}" dir="ltr">${Number(r.new_balance)<0?'-':''}${fmt(Math.abs(r.new_balance))}</td>
      </tr>`;
    }).join("");
    return `<div dir="rtl" style="font-family:Tajawal,sans-serif;background:#fff;color:#000;padding:6mm">
      <div style="text-align:center;border-bottom:3px solid #1E3A5F;padding-bottom:8px;margin-bottom:10px">
        <h1 style="margin:0;color:#1E3A5F">جُملَتي — إدارة مكتب الأمين</h1>
        <h2 style="margin:4px 0;color:#1E3A5F">كشف السندات اليومية — ${report.date}</h2>
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:10pt">
        <thead style="background:#1E3A5F;color:#fff">
          <tr>
            <th style="border:1px solid #000;padding:5px">#</th>
            <th style="border:1px solid #000;padding:5px">الوقت</th>
            <th style="border:1px solid #000;padding:5px">الزبون</th>
            <th style="border:1px solid #000;padding:5px">الهاتف</th>
            <th style="border:1px solid #000;padding:5px">المبلغ</th>
            <th style="border:1px solid #000;padding:5px">الخصم</th>
            <th style="border:1px solid #000;padding:5px">الرصيد بعد</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
        <tfoot style="background:#7ED321;color:#fff;font-weight:bold">
          <tr>
            <td colspan="4" style="border:1px solid #000;padding:6px;text-align:left">المجموع الكلي (${report.count} سند)</td>
            <td style="border:1px solid #000;padding:6px;text-align:left" dir="ltr">${fmt(report.total_amount)}</td>
            <td style="border:1px solid #000;padding:6px;text-align:left" dir="ltr">${fmt(report.total_discount)}</td>
            <td style="border:1px solid #000;padding:6px;text-align:left" dir="ltr">${fmt(report.grand_total)}</td>
          </tr>
        </tfoot>
      </table>
      <p style="margin-top:15px;font-size:9pt;color:#5C6E64">الإجمالي الكلي مع الخصومات: ${toArabicWords(report.grand_total)}</p>
    </div>`;
  };

  const printAll = () => {
    if (!report || report.count === 0) { toast.error("لا توجد سندات في هذا التاريخ"); return; }
    const styleLinks = Array.from(document.querySelectorAll('link[rel="stylesheet"], style')).map((el) => el.outerHTML).join("\n");
    const html = `<!DOCTYPE html><html dir="rtl" lang="ar"><head><meta charset="UTF-8"><title>كشف السندات ${report.date}</title>${styleLinks}<style>
      html,body{margin:0;padding:0;background:#fff;color:#000;font-family:'Tajawal',sans-serif}
      @page{size:A4;margin:8mm}
    </style></head><body>${reportHTML()}</body></html>`;
    printHTML(html, { title: `كشف السندات ${report.date}` });
  };

  const downloadReportPDF = async () => {
    if (!report || report.count === 0) { toast.error("لا توجد سندات في هذا التاريخ"); return; }
    try {
      await generatePDFFromHTML(reportHTML(), `كشف_السندات_${report.date}.pdf`);
      toast.success("تم تنزيل PDF");
    } catch {
      toast.error("فشل إنشاء PDF");
    }
  };

  return (
    <div data-testid="daily-report-section">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <label className="text-sm font-bold text-[#1E3A5F]">التاريخ:</label>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
          data-testid="report-date" dir="ltr"
          className="px-3 py-2 rounded-xl border-2 border-[#3B82F6] bg-white" />
        <button onClick={printAll} disabled={!report || report.count === 0}
          data-testid="report-print-all"
          className="bg-[#7ED321] hover:bg-[#6cb91d] text-white font-bold py-2 px-4 rounded-full flex items-center gap-2 disabled:opacity-50">
          <Printer className="w-4 h-4" />طباعة الكشف
        </button>
        <button onClick={downloadReportPDF} disabled={!report || report.count === 0}
          data-testid="report-pdf"
          className="bg-[#E85D45] hover:bg-[#c14a36] text-white font-bold py-2 px-4 rounded-full flex items-center gap-2 disabled:opacity-50">
          <FileDown className="w-4 h-4" />تنزيل PDF
        </button>
      </div>

      {report && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          <div className="bg-[#1E3A5F] text-white rounded-xl p-3">
            <p className="text-[10px] opacity-80">عدد السندات</p>
            <p className="font-black text-xl">{report.count}</p>
          </div>
          <div className="bg-[#7ED321] text-white rounded-xl p-3">
            <p className="text-[10px] opacity-90">المبلغ المُستلَم</p>
            <p className="font-black text-xl" dir="ltr">{fmt(report.total_amount)}</p>
          </div>
          <div className="bg-[#F5A623] text-white rounded-xl p-3">
            <p className="text-[10px] opacity-90">الخصم</p>
            <p className="font-black text-xl" dir="ltr">{fmt(report.total_discount)}</p>
          </div>
          <div className="bg-[#3B82F6] text-white rounded-xl p-3">
            <p className="text-[10px] opacity-90">الإجمالي</p>
            <p className="font-black text-xl" dir="ltr">{fmt(report.grand_total)}</p>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-center py-8 text-[#5C6E64]">جاري التحميل...</p>
      ) : !report || report.count === 0 ? (
        <p className="text-center py-8 text-[#5C6E64]">لا توجد سندات في هذا التاريخ</p>
      ) : (
        <div className="bg-white border-2 border-[#1E3A5F]/30 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#1E3A5F] text-white">
              <tr>
                <th className="p-2">#</th>
                <th className="p-2">الوقت</th>
                <th className="p-2 text-right">الزبون</th>
                <th className="p-2">الهاتف</th>
                <th className="p-2">المبلغ</th>
                <th className="p-2">الخصم</th>
                <th className="p-2">الرصيد بعد</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {report.receipts.map((r, i) => {
                // Convert UTC ISO → Baghdad wall clock (HH:mm). We derive
                // it once per row so the column stays cheap even for
                // busy days with 100+ receipts.
                const iraqTime = (() => {
                  if (!r.created_at) return "—";
                  try {
                    const d = new Date(r.created_at);
                    return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Baghdad" });
                  } catch { return "—"; }
                })();
                return (
                <tr key={r.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F0F9FF]"} data-testid={`receipt-row-${r.receipt_no}`}>
                  <td className="p-2 text-center font-bold">{r.receipt_no}</td>
                  <td className="p-2 text-center font-mono text-xs text-[#1E3A5F] font-bold" dir="ltr" data-testid={`receipt-time-${r.receipt_no}`}>{iraqTime}</td>
                  <td className="p-2 font-bold">{r.user_name}{r.market_name && <span className="text-[#5C6E64] text-xs"> — {r.market_name}</span>}{r.edited_at && <span className="text-[10px] text-[#F5A623] mr-1">(معدّل)</span>}</td>
                  <td className="p-2 text-center font-mono text-xs" dir="ltr">{r.user_phone || "—"}</td>
                  <td className="p-2 text-center font-bold text-[#7ED321]" dir="ltr">{fmt(r.amount)}</td>
                  <td className="p-2 text-center" dir="ltr">{fmt(r.discount || 0)}</td>
                  <td className={`p-2 text-center font-bold ${Number(r.new_balance) > 0 ? "text-[#E85D45]" : Number(r.new_balance) < 0 ? "text-[#1E3A5F]" : "text-[#7ED321]"}`} dir="ltr">
                    {Number(r.new_balance) < 0 ? "-" : ""}{fmt(Math.abs(r.new_balance))}
                  </td>
                  <td className="p-2 text-center flex gap-1 items-center justify-center">
                    <button onClick={() => setEditing(r)} data-testid={`edit-receipt-${r.receipt_no}`}
                      className="text-[#3B82F6] hover:bg-[#F0F9FF] rounded p-1" title="تعديل">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => printReceipt(r)} data-testid={`print-receipt-${r.receipt_no}`}
                      className="text-[#7ED321] hover:bg-[#F0FFEA] rounded p-1" title="طباعة">
                      <Printer className="w-4 h-4" />
                    </button>
                    <button onClick={() => downloadReceiptPDF(r)} data-testid={`pdf-receipt-${r.receipt_no}`}
                      className="text-[#E85D45] hover:bg-[#FEF2F2] rounded p-1" title="تنزيل PDF">
                      <FileDown className="w-4 h-4" />
                    </button>
                    <button onClick={() => shareReceiptWA(r)} data-testid={`wa-receipt-${r.receipt_no}`}
                      className="text-[#25D366] hover:bg-[#E8FCF1] rounded p-1" title="مشاركة واتساب">
                      <Share2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`⚠ حذف سند القبض رقم ${r.receipt_no}؟\nسيرجع دين الزبون كما كان قبل السند.`)) return;
                        try {
                          await axios.delete(`${API}/admin/receipts/${r.id}`);
                          toast.success(`✓ تم حذف السند #${r.receipt_no} — الدين تحدّث`);
                          load(date);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "فشل الحذف");
                        }
                      }}
                      data-testid={`delete-receipt-${r.receipt_no}`}
                      className="text-[#B91C1C] hover:bg-[#FEE2E2] rounded p-1"
                      title="حذف السند"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── Grand Total Box — sits at the bottom of the daily report,
              shows the FINAL total received for the day with icons ─── */}
      {report && report.count > 0 && (
        <div className="mt-4" data-testid="daily-receipts-grand-total">
          <div className="bg-gradient-to-l from-[#15803D] via-[#16a34a] to-[#22c55e] rounded-3xl p-5 shadow-2xl border-2 border-[#15803D]/40 relative overflow-hidden">
            {/* Decorative bg icons */}
            <Wallet className="absolute -top-4 -right-4 w-32 h-32 text-white/10 rotate-12" />
            <Receipt className="absolute -bottom-6 -left-6 w-28 h-28 text-white/10 -rotate-12" />

            <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-3 border border-white/30">
                  <TrendingUp className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="text-white/85 text-[11px] font-bold">مجموع الواصل النهائي لليوم</p>
                  <p className="text-white/70 text-[10px] mt-0.5" dir="ltr">
                    {new Date(date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                  </p>
                </div>
              </div>
              <div className="text-left md:text-center">
                <p className="text-white font-black text-4xl md:text-5xl leading-none tracking-tight" dir="ltr">
                  {fmt(report.total_amount)}
                </p>
                <p className="text-white/90 font-bold text-sm mt-1">دينار عراقي</p>
              </div>
            </div>

            {/* Sub-details row */}
            <div className="relative z-10 grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-white/20">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-white/85 text-[10px] font-bold">
                  <Receipt className="w-3.5 h-3.5" />
                  عدد السندات
                </div>
                <p className="text-white font-black text-lg mt-0.5">{report.count}</p>
              </div>
              <div className="text-center border-x border-white/20">
                <div className="flex items-center justify-center gap-1 text-white/85 text-[10px] font-bold">
                  <Wallet className="w-3.5 h-3.5" />
                  خصم اليوم
                </div>
                <p className="text-white font-black text-lg mt-0.5" dir="ltr">{fmt(report.total_discount)}</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-white/85 text-[10px] font-bold">
                  <TrendingUp className="w-3.5 h-3.5" />
                  الإجمالي مع الخصم
                </div>
                <p className="text-white font-black text-lg mt-0.5" dir="ltr">{fmt(report.grand_total)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {editing && <EditReceiptModal receipt={editing} onClose={() => setEditing(null)} onUpdated={() => load(date)} />}
    </div>
  );
};

// ===== Deleted Receipts (soft-delete) view =====
// Shows all receipts that were soft-deleted, sorted by deletion date.
// Admin can restore any receipt back to active state — the customer's
// debt reverts to what it was before the deletion.
const DeletedReceipts = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/receipts/deleted`);
      setItems(data || []);
    } catch { toast.error("فشل تحميل السندات المحذوفة"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const restore = async (r) => {
    if (!window.confirm(`استعادة السند #${r.receipt_no}؟\nسيتم خصم ${fmt(r.amount)} د.ع من دين الزبون مجدداً.`)) return;
    setRestoringId(r.id);
    try {
      await axios.post(`${API}/admin/receipts/${r.id}/restore`);
      toast.success(`✓ تم استعادة السند #${r.receipt_no}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الاستعادة");
    } finally { setRestoringId(null); }
  };

  return (
    <div className="space-y-3" data-testid="deleted-receipts-view">
      <div className="bg-[#FEE2E2] border-2 border-[#B91C1C]/30 rounded-2xl p-3">
        <p className="text-sm font-black text-[#B91C1C] flex items-center gap-2">
          <Trash2 className="w-5 h-5" />
          السندات المحذوفة ({items.length})
        </p>
        <p className="text-[11px] text-[#7f1d1d] mt-1">
          هذه سندات تم حذفها لكن محفوظة للمراجعة والتدقيق. يمكنك استعادة أيّ واحد وسيرجع دين الزبون كما لو أن السند موجود.
        </p>
      </div>
      {loading ? (
        <div className="text-center text-[#5C6E64] py-8">جاري التحميل...</div>
      ) : items.length === 0 ? (
        <div className="text-center text-[#5C6E64] py-12 bg-[#F8FAFC] rounded-2xl border-2 border-dashed border-[#E4EBE6]">
          <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
          لا توجد سندات محذوفة
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-2xl border-2 border-[#B91C1C]/20">
          <table className="w-full text-xs">
            <thead className="bg-[#B91C1C] text-white">
              <tr>
                <th className="p-2">رقم</th>
                <th className="p-2">الزبون</th>
                <th className="p-2">المبلغ</th>
                <th className="p-2">الخصم</th>
                <th className="p-2">تاريخ الحذف</th>
                <th className="p-2">حذفه</th>
                <th className="p-2">إجراء</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r, i) => (
                <tr key={r.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FFF5F5]"} data-testid={`deleted-receipt-${r.receipt_no}`}>
                  <td className="p-2 text-center font-bold">{r.receipt_no}</td>
                  <td className="p-2 font-bold">
                    {r.user_name}
                    {r.market_name && <span className="text-[#5C6E64] text-[10px]"> — {r.market_name}</span>}
                  </td>
                  <td className="p-2 text-center font-bold text-[#B91C1C] line-through" dir="ltr">{fmt(r.amount)}</td>
                  <td className="p-2 text-center line-through" dir="ltr">{fmt(r.discount || 0)}</td>
                  <td className="p-2 text-center text-[10px] text-[#5C6E64]" dir="ltr">
                    {r.deleted_at ? new Date(r.deleted_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }) : "—"}
                  </td>
                  <td className="p-2 text-center text-[10px]">{r.deleted_by_name || "أدمن"}</td>
                  <td className="p-2 text-center">
                    <button
                      onClick={() => restore(r)}
                      disabled={restoringId === r.id}
                      data-testid={`restore-receipt-${r.receipt_no}`}
                      className="bg-[#15803D] hover:bg-[#0f6630] disabled:opacity-50 text-white text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 mx-auto"
                    >
                      <RotateCcw className="w-3 h-3" />
                      استعادة
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ===== Main component =====
const ReceiptVoucher = () => {
  const [mode, setMode] = useState("create"); // create | report

  return (
    <div className="space-y-4" data-testid="receipt-voucher" dir="rtl">
      <div className="bg-white rounded-2xl p-4 border-2 border-[#1E3A5F]/20 shadow-md">
        <div className="flex items-center justify-between mb-4 pb-3 border-b-2 border-[#1E3A5F]/20 flex-wrap gap-2">
          <h3 className="font-heading font-bold text-xl text-[#1E3A5F] flex items-center gap-2">
            <FileText className="w-5 h-5" />سند قبض
          </h3>
          <div className="text-sm text-[#5C6E64]">
            <Calendar className="w-4 h-4 inline ml-1" />{todayStr()}
          </div>
        </div>

        <div className="flex gap-2 mb-4 flex-wrap">
          <button onClick={() => setMode("create")} data-testid="tab-create"
            className={`px-4 py-2 rounded-full font-bold text-sm transition-colors ${mode === "create" ? "bg-[#1E3A5F] text-white" : "bg-[#F3F6F4] text-[#5C6E64]"}`}>
            إنشاء سند جديد
          </button>
          <button onClick={() => setMode("report")} data-testid="tab-report"
            className={`px-4 py-2 rounded-full font-bold text-sm transition-colors ${mode === "report" ? "bg-[#1E3A5F] text-white" : "bg-[#F3F6F4] text-[#5C6E64]"}`}>
            كشف السندات اليومية
          </button>
          <button onClick={() => setMode("deleted")} data-testid="tab-deleted"
            className={`px-4 py-2 rounded-full font-bold text-sm transition-colors flex items-center gap-1 ${mode === "deleted" ? "bg-[#B91C1C] text-white" : "bg-[#FEE2E2] text-[#B91C1C]"}`}>
            <Trash2 className="w-4 h-4" />
            السندات المحذوفة
          </button>
        </div>

        {mode === "create" && <CreateReceipt />}
        {mode === "report" && <DailyReport />}
        {mode === "deleted" && <DeletedReceipts />}
      </div>
    </div>
  );
};

export default ReceiptVoucher;
